import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Trophy, ChatCircleText, CheckCircle, Users, CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "7 days" },
  { key: "month", label: "30 days" },
  { key: "all", label: "All-time" },
];

function medal(rank) {
  if (rank === 1) return { label: "🥇", ring: "ring-[#FBBF24]" };
  if (rank === 2) return { label: "🥈", ring: "ring-[#9CA3AF]" };
  if (rank === 3) return { label: "🥉", ring: "ring-[#D97706]" };
  return { label: `#${rank}`, ring: "ring-[var(--border)]" };
}

function initials(name) {
  const src = (name || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function AgentStatsDialog({ open, onOpenChange }) {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (p) => {
    setLoading(true);
    api
      .get(`/stats/agents?period=${p}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load(period);
  }, [open, period]);

  const agents = data?.agents || [];
  const totals = data?.totals || { messages_sent: 0, tickets_closed: 0, active_agents: 0 };
  const maxMsgs = Math.max(1, ...agents.map((a) => a.messages_sent));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="agent-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 heading">
            <Trophy size={22} weight="fill" className="text-[#FBBF24]" />
            Agent Performance
          </DialogTitle>
          <DialogDescription>
            Leaderboard of every team member's activity. Ranked by tickets closed, then messages sent.
          </DialogDescription>
        </DialogHeader>

        {/* Period tabs */}
        <div className="flex items-center gap-1 text-xs">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                period === p.key
                  ? "bg-[var(--fg)] text-[var(--bg)] font-medium"
                  : "hover:bg-[var(--bg)] text-[var(--muted)]"
              }`}
              data-testid={`agent-stats-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded-md border border-[var(--border)] p-3 flex items-center gap-2">
            <ChatCircleText size={18} className="text-[#60A5FA]" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--muted)]">Messages</div>
              <div className="text-lg font-semibold heading leading-tight">{totals.messages_sent}</div>
            </div>
          </div>
          <div className="rounded-md border border-[var(--border)] p-3 flex items-center gap-2">
            <CheckCircle size={18} className="text-[#10B981]" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--muted)]">Tickets closed</div>
              <div className="text-lg font-semibold heading leading-tight">{totals.tickets_closed}</div>
            </div>
          </div>
          <div className="rounded-md border border-[var(--border)] p-3 flex items-center gap-2">
            <Users size={18} className="text-[#F59E0B]" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--muted)]">Active agents</div>
              <div className="text-lg font-semibold heading leading-tight">{totals.active_agents}</div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        {loading ? (
          <div className="py-10 flex items-center justify-center text-[var(--muted)] text-sm gap-2">
            <CircleNotch size={18} className="animate-spin" /> Loading…
          </div>
        ) : agents.length === 0 ? (
          <div className="py-10 text-center text-sm text-[var(--muted)]">No data for this period.</div>
        ) : (
          <div className="space-y-2">
            {agents.map((a) => {
              const m = medal(a.rank);
              const barWidth = `${Math.round((a.messages_sent / maxMsgs) * 100)}%`;
              return (
                <div
                  key={a.agent_id}
                  className="rounded-md border border-[var(--border)] p-3 flex items-center gap-3"
                  data-testid={`agent-row-${a.agent_id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ring-2 ${m.ring} bg-[var(--bg)] shrink-0`}>
                    <span className="text-base">{m.label}</span>
                  </div>
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
                      {initials(a.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm truncate">{a.full_name}</div>
                      {a.role && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--muted)] uppercase">
                          {a.role}
                        </span>
                      )}
                    </div>
                    {a.email && (
                      <div className="text-[11px] text-[var(--muted)] truncate">{a.email}</div>
                    )}
                    {/* Messages bar */}
                    <div className="mt-1.5 h-1.5 bg-[var(--bg)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#60A5FA] rounded-full transition-all"
                        style={{ width: barWidth }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs shrink-0 pl-2">
                    <div className="text-center">
                      <div className="font-semibold text-sm heading leading-tight">{a.messages_sent}</div>
                      <div className="text-[10px] text-[var(--muted)]">msgs</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-sm heading leading-tight">{a.conversations_handled}</div>
                      <div className="text-[10px] text-[var(--muted)]">convos</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-sm heading leading-tight text-[#10B981]">
                        {a.tickets_closed}
                      </div>
                      <div className="text-[10px] text-[var(--muted)]">closed</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-[11px] text-[var(--muted)] pt-1 border-t border-[var(--border)]">
          Tip: Ticket close karne pe automatic count badhega. Bot aur System messages exclude kiye gaye hain.
        </div>
      </DialogContent>
    </Dialog>
  );
}
